import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createMatch,
  createTeam,
  discardSetup,
  loadSetupContext,
  loadTeamContext,
  startCapture,
  updateSetup,
} from '../../app/preMatch';
import type { SetupContext, TeamContext } from '../../app/preMatch';
import { loadOpponentPrep } from '../../app/opponentPrep';
import type { OpponentPrep } from '../../app/opponentPrep';
import type { MatchSetupPatch } from '../../domain/commands';
import { config } from '../../config';
import { newId } from '../../app/ids';
import { log } from '../../app/logger';
import { recordSurface, SURFACE } from '../../app/resume';
import {
  candidateFromCarry,
  candidateFromFreeText,
  candidateFromTemplateEntry,
  mergePreSelectionWithCarried,
  normalizeHypothesisText,
  preSelectableEntries,
} from '../../domain/setup';
import type { CarryCandidate, PreMatchCandidate } from '../../domain/setup';
import type { HypothesisPhase, TemplateEntry, TemplateRow } from '../../types';

/**
 * B spec §2.4 — **the match setup flow's controller** (build unit B-7): state, effects and
 * dispatch, no rendering. The A precedent (`usePreMatchSetup`) is the engine-wiring reference,
 * and every *decision* it consumed stays consumed from the same places — `domain/setup.ts` owns
 * the pre-selection, the merge and the candidate constructors; `domain/commands/setup` owns
 * every guard; `app/preMatch.ts` owns the reads and dispatches. What is B's own here is the
 * screen shape: one sectioned screen (no `chooseTeam` phase — the opponent is section 1), the
 * F-2 purpose gate, the scenario-34 prep block, and no locked-start exit (scenario 6: **every**
 * B match starts in discovery, so `startCapture(…, 'discovery', [])` is the only exit and
 * PRE-7's board chooser has no screen to be on).
 *
 * ── The action queue (the `usePreMatchSetup` race, kept closed) ──────────────
 *
 * Every action is serialized through one promise chain. `busy` disables controls only after
 * React commits a render, and a second tap can land in that gap; both handlers would then read
 * the same committed row and one write silently wins. A's browser pass caught it on the SES-5
 * stepper (fifteen taps moved the value fourteen), and the stepper here has exactly the same
 * shape — so the fix travels with it: actions chain, and each stepper tap re-reads the
 * committed value before patching (a readonly transaction per tap on a screen with no latency
 * budget). A rejection never leaves the chain: the user sees the sentence, the log names the
 * action (NFR-8).
 *
 * ── PRE-9 × PRE-5 — the initial selection, computed once per (match, opponent) ───────────────
 *
 * Seeds (`preSelectableEntries` — slot-free builtin entries) and carried candidates (PRE-5's
 * pre-checked offer) are merged by `mergePreSelectionWithCarried` into the one list this screen
 * starts from — §6.0b's "computed once on mount and thereafter the user's selection state", so
 * a deselection is never resurrected by a re-read. Two B-specific edges:
 *
 * - **Keyed on (matchId, teamId), and re-keying REPLACES the selection**: B's one-screen shape
 *   lets the opponent change mid-setup, and carried candidates from the previous opponent must
 *   not survive into the new one's prep context — the default is the default, per opponent.
 * - **Guarded on the seed templates having arrived**: `bootstrapApp()` installs the library
 *   fire-and-forget at boot, and a resume straight into setup can read `templates` before the
 *   install commits. Pre-selecting against an empty library would lock in an empty default
 *   (the once-guard would then refuse to repair it), so the computation waits for a read that
 *   carries templates and runs on the next refresh instead.
 */

export type SetupPhase = 'loading' | 'ready' | 'failed';

const MODULE = 'ui-b/setup';

export interface SetupState {
  readonly phase: SetupPhase;
  readonly context: SetupContext | null;
  readonly teamContext: TeamContext | null;
  readonly prep: OpponentPrep | null;
  readonly selected: readonly PreMatchCandidate[];
  /** A rejection or refusal, surfaced as an ordinary sentence (`role="status"`). */
  readonly notice: string | null;
  readonly busy: boolean;
  /** Completed context reads — the `useHomeB` waiter device, rendered as `data-read`. */
  readonly reads: number;
}

export function useSetup(
  /** Called after `StartCapture` has committed and the surface hint is written (PRE-7's exit). */
  onStarted: (matchId: string) => void,
) {
  const [context, setContext] = useState<SetupContext | null>(null);
  const [teamContext, setTeamContext] = useState<TeamContext | null>(null);
  const [prep, setPrep] = useState<OpponentPrep | null>(null);
  const [selected, setSelected] = useState<readonly PreMatchCandidate[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reads, setReads] = useState(0);
  const loaded = useRef(false);
  /** The (matchId, teamId) the pre-selection last ran for — the once-guard. */
  const preSelectedFor = useRef<string | null>(null);

  const queue = useRef<Promise<void>>(Promise.resolve());
  const run = useCallback((label: string, work: () => Promise<void>): Promise<void> => {
    const next = queue.current.then(async () => {
      setBusy(true);
      try {
        await work();
      } catch (cause) {
        const message = cause instanceof Error ? cause.message : String(cause);
        setNotice(message);
        log.error(MODULE, `setup action failed: ${label}`, { message });
      } finally {
        setBusy(false);
      }
    });
    // The chain must never inherit a rejection, or one failure stalls every later action.
    queue.current = next.catch(() => undefined);
    return next;
  }, []);

  /**
   * The opponent's prep context: PRE-5's carry-forward + scenario 34's emphasis card, read
   * together, then the initial selection if this (match, opponent) has not had one.
   */
  const loadTeamFor = useCallback(
    async (matchId: string, teamId: string, templates: readonly TemplateRow[]): Promise<void> => {
      const [team, emphasis] = await Promise.all([
        loadTeamContext(teamId, matchId),
        loadOpponentPrep(teamId),
      ]);
      setTeamContext(team);
      setPrep(emphasis ?? null);

      // See the header: once per (match, opponent), and never against an empty library.
      const key = `${matchId}:${teamId}`;
      if (preSelectedFor.current === key || templates.length === 0) return;
      preSelectedFor.current = key;

      const seeds = preSelectableEntries(templates).map((seed) =>
        candidateFromTemplateEntry(newId(), seed.templateId, seed.entry, seed.entry.text, null),
      );
      const carried = team.carryForward
        .filter((candidate) => candidate.preChecked)
        .map((candidate) => candidateFromCarry(newId(), candidate));
      setSelected(mergePreSelectionWithCarried(seeds, carried));
    },
    [],
  );

  const refresh = useCallback(async (): Promise<SetupContext> => {
    const next = await loadSetupContext();
    setContext(next);
    if (next.setup !== undefined) {
      await loadTeamFor(next.setup.id, next.setup.teamId, next.templates);
    }
    setReads((count) => count + 1);
    return next;
  }, [loadTeamFor]);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    void run('load', async () => {
      const next = await refresh();
      if (next.setup !== undefined) await recordSurface(next.setup.id, SURFACE.setup);
    }).catch(() => {
      setFailure(true);
    });
  }, [refresh, run]);

  const setup = context?.setup;

  // ── Section 1 — opponent ───────────────────────────────────────────────────

  /** Picks (or switches to) an opponent; creates the `preMatch` row on the first pick (§2.2). */
  const pickOpponent = useCallback(
    (teamId: string) =>
      run('pickOpponent', async () => {
        const current = (await loadSetupContext()).setup;
        const result =
          current === undefined
            ? await createMatch({ teamId })
            : await updateSetup(current.id, { teamId });
        if (!result.ok) {
          setNotice(result.message);
          await refresh();
          return;
        }
        const next = await refresh();
        if (next.setup !== undefined) await recordSurface(next.setup.id, SURFACE.setup);
      }),
    [refresh, run],
  );

  /** PRE-1 — create the opponent, then continue setup against them in the same action. */
  const createOpponent = useCallback(
    (name: string, club: string, ageGroup: string): Promise<void> =>
      run('createOpponent', async () => {
        const created = await createTeam({
          name,
          club: club.trim() === '' ? null : club.trim(),
          ageGroup: ageGroup.trim() === '' ? null : ageGroup.trim(),
        });
        if (!created.ok) {
          setNotice(created.message);
          return;
        }
        const current = (await loadSetupContext()).setup;
        const result =
          current === undefined
            ? await createMatch({ teamId: created.id })
            : await updateSetup(current.id, { teamId: created.id });
        if (!result.ok) {
          setNotice(result.message);
          await refresh();
          return;
        }
        const next = await refresh();
        if (next.setup !== undefined) await recordSurface(next.setup.id, SURFACE.setup);
      }),
    [refresh, run],
  );

  // ── Sections 1–3 — the setup patch controls ────────────────────────────────

  const editSetup = useCallback(
    (patch: MatchSetupPatch) =>
      run('editSetup', async () => {
        const current = (await loadSetupContext()).setup;
        if (current === undefined) return;
        const result = await updateSetup(current.id, patch);
        // On refusal the control still shows what the user picked; the re-read is the repair.
        if (!result.ok) setNotice(result.message);
        await refresh();
      }),
    [refresh, run],
  );

  /** SES-5's stepper as a relative step against the committed value (see the header). */
  const stepHalfLength = useCallback(
    (delta: number) =>
      run('stepHalfLength', async () => {
        const current = (await loadSetupContext()).setup;
        if (current === undefined) return;
        const next = current.halfLengthMin + delta;
        if (next < config.HALF_LENGTH_MIN_MIN || next > config.HALF_LENGTH_MIN_MAX) return;
        const result = await updateSetup(current.id, { halfLengthMin: next });
        if (!result.ok) setNotice(result.message);
        await refresh();
      }),
    [refresh, run],
  );

  // ── Sections 4–5 — the selection ───────────────────────────────────────────

  /** Removes the one candidate holding this normalized key (seed, carried or custom alike). */
  const deselectKey = useCallback((key: string) => {
    setSelected((current) =>
      current.filter((candidate) => normalizeHypothesisText(candidate.text) !== key),
    );
  }, []);

  /** PRE-2 — re-select a library entry (they all arrive selected; this is the way back in). */
  const selectSeed = useCallback((templateId: string, entry: TemplateEntry) => {
    setSelected((current) => [
      ...current,
      candidateFromTemplateEntry(newId(), templateId, entry, entry.text, null),
    ]);
  }, []);

  /** PRE-5 — carry a prior pattern forward, F-21 snapshot and all. */
  const selectCarry = useCallback((carry: CarryCandidate) => {
    setSelected((current) => [...current, candidateFromCarry(newId(), carry)]);
  }, []);

  /** PRE-3 — free text plus a phase picker. Refuses in the sheet; this only constructs. */
  const addCustom = useCallback((text: string, phase: HypothesisPhase) => {
    const trimmed = text.trim();
    if (trimmed === '') return;
    setSelected((current) => [...current, candidateFromFreeText(newId(), trimmed, phase)]);
  }, []);

  const removeCandidate = useCallback((id: string) => {
    setSelected((current) => current.filter((candidate) => candidate.id !== id));
  }, []);

  // ── Section 7 — the exits ──────────────────────────────────────────────────

  /**
   * F-2's UI-level required gate, as a **behavior**: applied on the tap as well as rendered as
   * the disabled Begin control, so a tap that slips past the attribute still refuses — and the
   * engine's `ERR_MATCH_FORMAT_REQUIRED` stays the backstop behind both.
   */
  const begin = useCallback(
    () =>
      run('begin', async () => {
        const current = (await loadSetupContext()).setup;
        if (current === undefined) return;
        if (current.format === null || current.purpose === null) {
          setNotice('Choose a format and a purpose first — neither has a default.');
          return;
        }
        const result = await startCapture(current.id, selected, 'discovery', []);
        if (!result.ok) {
          setNotice(result.message);
          return;
        }
        // SES-3 — recorded on exit, so a kill one millisecond later resumes into the same
        // place the shell is about to navigate to (scenario 6: discovery, pre-kickoff).
        await recordSurface(current.id, SURFACE.discovery);
        onStarted(current.id);
      }),
    [onStarted, run, selected],
  );

  /** §2.2 — `DiscardSetup`, the guarded escape hatch. The two-step arm lives in the screen. */
  const discard = useCallback(
    (onDiscarded: () => void) =>
      run('discard', async () => {
        const current = (await loadSetupContext()).setup;
        if (current === undefined) {
          onDiscarded();
          return;
        }
        const result = await discardSetup(current.id);
        if (!result.ok) {
          setNotice(result.message);
          return;
        }
        preSelectedFor.current = null;
        setSelected([]);
        setTeamContext(null);
        setPrep(null);
        onDiscarded();
      }),
    [run],
  );

  const dismissNotice = useCallback(() => {
    setNotice(null);
  }, []);

  const phase: SetupPhase = failure ? 'failed' : context === null ? 'loading' : 'ready';

  return {
    state: { phase, context, teamContext, prep, selected, notice, busy, reads } satisfies SetupState,
    setup,
    actions: {
      pickOpponent,
      createOpponent,
      editSetup,
      stepHalfLength,
      selectSeed,
      selectCarry,
      addCustom,
      deselectKey,
      removeCandidate,
      begin,
      discard,
      dismissNotice,
    },
  };
}

export type SetupActions = ReturnType<typeof useSetup>['actions'];
